import React, { useEffect, useState } from "react";
import axios from "axios";
import { X, Check, ExternalLink, AlertTriangle } from "lucide-react";
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid,
} from "recharts";
import { fmtMoneyFull, fmtM, fmtPct, fmtNum } from "../lib/format";
import ReportGroups from "./ReportGroups";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CHART_MARGIN = { top: 8, right: 8, left: 0, bottom: 0 };
const TT_CONTENT = { background: "#fff", border: "1px solid #D2E0E6", fontFamily: "monospace", fontSize: 12, color: "#0B2830" };
const TT_LABEL = { color: "#47616B" };
const TT_ITEM = { color: "#0B2830" };
const AXIS_TICK = { fill: "#7C949C", fontSize: 10 };
const AXIS_LINE = { stroke: "#D2E0E6" };
const Y_DOMAIN = ["auto", "auto"];

const Metric = ({ label, value, color }) => (
  <div className="bg-[#E9F1F4] border border-[#D2E0E6] rounded-md p-3">
    <div className="text-[10px] uppercase tracking-[0.12em] text-[#7C949C] mb-1">{label}</div>
    <div className="mono text-base font-semibold" style={{ color: color || "#0B2830" }}>{value}</div>
  </div>
);

export default function StockDetail({ stock, onClose, token, period = "quarterly" }) {
  const [price, setPrice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [report, setReport] = useState(null);
  const [reportLoading, setReportLoading] = useState(true);
  const reportUrl = `/report/${stock.ticker}?period=${period}`;

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/price/${stock.ticker}`, { headers: token ? { Authorization: `Bearer ${token}` } : {} })
      .then((r) => setPrice(r.data))
      .catch(() => setPrice({ series: [] }))
      .finally(() => setLoading(false));
  }, [stock.ticker, token]);

  useEffect(() => {
    setReportLoading(true);
    setReport(null);
    axios.get(`${API}/stock/${stock.ticker}/report`, { params: { period }, headers: token ? { Authorization: `Bearer ${token}` } : {} })
      .then((r) => setReport(r.data))
      .catch(() => setReport(null))
      .finally(() => setReportLoading(false));
  }, [stock.ticker, token, period]);

  const series = (price?.series || []).map((p) => ({
    date: new Date(p.t).toLocaleDateString(undefined, { month: "short", year: "2-digit" }),
    close: p.close,
  }));
  const first = series[0]?.close;
  const last = series[series.length - 1]?.close;
  const change = first && last ? ((last - first) / first) * 100 : null;
  const chg = change >= 0;
  const lineColor = chg ? "#0D9488" : "#E11D48";

  return (
    <div className="fixed inset-0 bg-[#0B2830]/40 flex justify-end z-50" data-testid="stock-detail" onClick={onClose}>
      <div className="bg-white border-l border-[#D2E0E6] w-full max-w-2xl h-full overflow-y-auto fade-up"
        onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between border-b border-[#D2E0E6] px-6 py-5 sticky top-0 bg-white z-10">
          <div>
            <div className="flex items-center gap-2">
              <h2 className="text-2xl font-bold mono text-[#0B2830]">{stock.ticker}</h2>
              {stock.qualifies && (
                <span className="inline-flex items-center gap-1 bg-[#059669]/10 text-[#059669] px-2 py-0.5 text-[10px] mono border border-[#059669]/25 rounded">
                  <Check className="w-3 h-3" /> QUALIFIES
                </span>
              )}
              {stock.beats_peers && (
                <span className="bg-[#0284C7]/10 text-[#0284C7] px-2 py-0.5 text-[10px] mono border border-[#0284C7]/25 rounded">BEATS PEERS</span>
              )}
            </div>
            <div className="text-sm text-[#47616B] mt-0.5">{stock.name}</div>
            <div className="text-xs text-[#7C949C] mt-0.5">{stock.sector} &middot; {stock.industry} &middot; {stock.country}</div>
          </div>
          <button data-testid="detail-close" onClick={onClose} className="text-[#7C949C] hover:text-[#0B2830]">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-6 pt-4">
          {stock.risk_flag && (
            <div data-testid="risk-flag-banner" className="mb-3 flex items-start gap-2 bg-[#E11D48]/8 border border-[#E11D48]/30 rounded-md px-3 py-2.5">
              <AlertTriangle className="w-4 h-4 text-[#E11D48] mt-0.5 shrink-0" />
              <div>
                <div className="text-xs font-semibold text-[#E11D48] uppercase tracking-[0.1em] mb-0.5">
                  Integrity flag &middot; {stock.risk_flag.level}
                </div>
                <div className="text-xs text-[#47616B] leading-relaxed">{stock.risk_flag.reason}</div>
                <div className="text-[10px] text-[#94A7AE] mt-1">Publicly reported allegations/litigation &mdash; not a determination of wrongdoing.</div>
              </div>
            </div>
          )}
          <a href={reportUrl} target="_blank" rel="noopener noreferrer" data-testid="open-report-newtab"
            className="inline-flex items-center gap-1.5 bg-[#0284C7] text-white px-3 py-1.5 rounded-md text-xs font-medium hover:bg-[#0369A1] transition-colors">
            <ExternalLink className="w-3.5 h-3.5" /> Open full report in new tab
          </a>
        </div>

        <div className="p-6 space-y-6">
          {/* Price chart */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-xs uppercase tracking-[0.15em] text-[#7C949C]">Share price &middot; last 2 years {price?.currency ? `(${price.currency})` : ""}</h3>
              {change != null && (
                <span className="mono text-sm font-semibold" style={{ color: lineColor }}>
                  {fmtPct(change)} 2Y
                </span>
              )}
            </div>
            <div className="h-64 bg-[#F5FAFB] border border-[#D2E0E6] rounded-md p-2" data-testid="price-chart">
              {loading ? (
                <div className="h-full flex items-center justify-center text-[#7C949C] text-sm">Loading price history&hellip;</div>
              ) : series.length === 0 ? (
                <div className="h-full flex items-center justify-center text-[#7C949C] text-sm">Price history unavailable.</div>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={series} margin={CHART_MARGIN}>
                    <defs>
                      <linearGradient id="pg" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor={lineColor} stopOpacity={0.3} />
                        <stop offset="100%" stopColor={lineColor} stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid stroke="#E6EEF1" vertical={false} />
                    <XAxis dataKey="date" tick={AXIS_TICK} minTickGap={40} axisLine={AXIS_LINE} tickLine={false} />
                    <YAxis tick={AXIS_TICK} width={48} axisLine={false} tickLine={false} domain={Y_DOMAIN} />
                    <Tooltip contentStyle={TT_CONTENT}
                      labelStyle={TT_LABEL} itemStyle={TT_ITEM} />
                    <Area type="monotone" dataKey="close" stroke={lineColor} strokeWidth={1.5} fill="url(#pg)" />
                  </AreaChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>

          {/* KPI grid */}
          <div className="grid grid-cols-3 gap-3">
            <Metric label="Score" value={fmtNum(stock.score)} color="#0284C7" />
            <Metric label="Market Cap" value={fmtMoneyFull(stock.marketCap)} />
            <Metric label="P/E" value={fmtNum(stock.peRatio)} />
            <Metric label="Peer Median P/E" value={stock.sector_median_pe != null ? fmtNum(stock.sector_median_pe) : "\u2014"} color="#47616B" />
            <Metric label="Forward P/E" value={stock.forwardPE != null ? fmtNum(stock.forwardPE) : "\u2014"} />
            <Metric label="Peer Fwd P/E" value={stock.sector_median_fwd_pe != null ? fmtNum(stock.sector_median_fwd_pe) : "\u2014"} color="#47616B" />
            <Metric label="Fwd P/E \u00F7 Peer" value={stock.fwd_pe_vs_peer != null ? fmtNum(stock.fwd_pe_vs_peer, 2) : "\u2014"} color={stock.fwd_pe_vs_peer != null && stock.fwd_pe_vs_peer <= 1 ? "#059669" : "#47616B"} />
            <Metric label="PEG (Fwd)" value={stock.forward_peg != null ? fmtNum(stock.forward_peg, 2) : "\u2014"} />
            <Metric label="PEG 2Y / 3Y / 5Y" value="N/A" color="#94A7AE" />
            <Metric label="EBITDA (TTM)" value={fmtM(stock.ebitda_ttm_m)} />
            <Metric label="EBITDA Margin" value={stock.ebitda_margin != null ? `${fmtNum(stock.ebitda_margin)}%` : "\u2014"} />
            <Metric label="EBITDA YoY" value={fmtPct(stock.ebitda_yoy)} color={stock.ebitda_yoy >= 0 ? "#059669" : "#E11D48"} />
            <Metric label="EBITDA 18M Growth" value={fmtPct(stock.ebitda_growth_18m)} color={stock.ebitda_growth_18m >= 0 ? "#059669" : "#E11D48"} />
            <Metric label="Dividend Payout" value={stock.payout_ratio != null ? `${fmtNum(stock.payout_ratio)}%` : "\u2014"} />
            <Metric label="Buyback 3M" value={fmtM(stock.buyback_3m_m)} />
            <Metric label="Buyback 12M" value={fmtM(stock.buyback_12m_m)} color="#0D9488" />
            <Metric label="Buyback Yield" value={stock.buyback_yield != null ? `${fmtNum(stock.buyback_yield, 2)}%` : "\u2014"} color="#D97706" />
          </div>

          {/* EBITDA quarterly series */}
          {stock.ebitda_series?.length > 0 && (
            <div>
              <h3 className="text-xs uppercase tracking-[0.15em] text-[#7C949C] mb-3">Quarterly EBITDA (USD, last 18 months)</h3>
              <div className="h-40 bg-[#F5FAFB] border border-[#D2E0E6] rounded-md p-2">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={stock.ebitda_series.map((d) => ({ period: d.period, ebitda: d.ebitda }))}
                    margin={CHART_MARGIN}>
                    <CartesianGrid stroke="#E6EEF1" vertical={false} />
                    <XAxis dataKey="period" tick={AXIS_TICK} axisLine={AXIS_LINE} tickLine={false} />
                    <YAxis tick={AXIS_TICK} width={48} axisLine={false} tickLine={false} />
                    <Tooltip contentStyle={TT_CONTENT}
                      labelStyle={TT_LABEL} itemStyle={TT_ITEM} />
                    <Area type="monotone" dataKey="ebitda" stroke="#0284C7" strokeWidth={1.5} fill="#0284C7" fillOpacity={0.12} />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}
          {/* Detailed financial report */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-xs uppercase tracking-[0.15em] text-[#7C949C]">
                Financial report &middot; last {period === "quarterly" ? "8 quarters" : "5 fiscal years"}
              </h3>
            </div>
            {reportLoading ? (
              <div className="text-sm text-[#7C949C] py-6 text-center">Loading financial metrics&hellip;</div>
            ) : report ? (
              <ReportGroups report={report} />
            ) : (
              <div className="text-sm text-[#7C949C] py-6 text-center">Detailed report unavailable for this ticker.</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
